import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Helmet } from '@dr.pogodin/react-helmet';
import { PlusIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { css, html } from 'react-strict-dom';

import { Api, useApiCallable } from '../../../api';
import { getApiErrorMessage } from '../../../api/hub-api-request';
import {
  PageError,
  PageLoading,
  PageWrap,
  Stack,
  Text,
} from '../../../components';
import { Color } from '../../../constants';
import { color } from '../../../constants/color.css';
import { useCompany } from '../../../contexts/ClientDataContext';
import { useAuthUser } from '../../../hooks';
import { isAgency } from '../../../reducers/auth';
import {
  EMPTY_COPY,
  type Idea,
  type MoveDestination,
  type WishlistColumn,
  canReorderColumn,
  ideasForColumn,
  moveIdeaRequest,
  positionForDrop,
  serviceOrderIdeas,
} from '../board';
import BoardColumn from './BoardColumn';
import CancelIdeaModal from './CancelIdeaModal';
import ColumnSwitcher from './ColumnSwitcher';
import IdeaCard, { IdeaCardContent } from './IdeaCard';
import LinkServiceOrderModal from './LinkServiceOrderModal';
import NewIdeaModal from './NewIdeaModal';
import ServiceOrderCard from './ServiceOrderCard';

type ReorderableColumn = 'idea' | 'preparing';
type ReorderableColumns = Record<ReorderableColumn, Array<Idea>>;

const EMPTY_COLUMNS: ReorderableColumns = { idea: [], preparing: [] };

export default function WishlistPage() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const company = useCompany(companySlug!);
  const authUser = useAuthUser();
  const navigate = useNavigate();
  const callApi = useApiCallable();
  const agency = isAgency(authUser);

  const api = Api.CompanyWishlist.useReadItemWishlist(company.id);

  const [columns, setColumns] = React.useState<ReorderableColumns | null>(null);
  const columnsRef = React.useRef<ReorderableColumns>(EMPTY_COLUMNS);
  const snapshotRef = React.useRef<ReorderableColumns | null>(null);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [mobileColumn, setMobileColumn] =
    React.useState<WishlistColumn>('idea');
  const [newIdeaOpen, setNewIdeaOpen] = React.useState(false);
  const [linkingIdea, setLinkingIdea] = React.useState<Idea | null>(null);
  const [cancellingIdea, setCancellingIdea] = React.useState<Idea | null>(null);

  const updateColumns = (next: ReorderableColumns) => {
    columnsRef.current = next;
    setColumns(next);
  };

  React.useEffect(() => {
    if (!api.data) return;
    updateColumns({
      idea: ideasForColumn(api.data.ideas, 'idea'),
      preparing: ideasForColumn(api.data.ideas, 'preparing'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const findColumn = (id: string): ReorderableColumn | null => {
    if (id === 'idea' || id === 'preparing') return id;
    if (columnsRef.current.idea.some((idea) => idea.id === id)) return 'idea';
    if (columnsRef.current.preparing.some((idea) => idea.id === id)) {
      return 'preparing';
    }
    return null;
  };

  const openIdea = (idea: Idea) =>
    navigate(`/company/${companySlug}/wishlist/${idea.id}`);

  const ideaHref = (idea: Idea) =>
    `/company/${companySlug}/wishlist/${idea.id}`;

  const onDragStart = (event: DragStartEvent) => {
    snapshotRef.current = {
      idea: [...columnsRef.current.idea],
      preparing: [...columnsRef.current.preparing],
    };
    setActiveId(String(event.active.id));
  };

  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeColumn = findColumn(String(active.id));
    const overColumn = findColumn(String(over.id));
    if (!activeColumn || !overColumn || activeColumn === overColumn) return;
    if (
      !canReorderColumn(activeColumn, agency) ||
      !canReorderColumn(overColumn, agency)
    ) {
      return;
    }

    const current = columnsRef.current;
    const activeIdea = current[activeColumn].find(
      (idea) => idea.id === active.id
    );
    if (!activeIdea) return;

    const remaining = current[activeColumn].filter(
      (idea) => idea.id !== active.id
    );
    const target = [...current[overColumn]];
    const overIndex = target.findIndex((idea) => idea.id === over.id);
    target.splice(overIndex >= 0 ? overIndex : target.length, 0, {
      ...activeIdea,
      state: overColumn,
    });

    updateColumns({
      ...current,
      [activeColumn]: remaining,
      [overColumn]: target,
    });
  };

  const reorderIdea = (
    ideaId: string,
    column: ReorderableColumn,
    ideas: Array<Idea>
  ) => {
    const index = ideas.findIndex((idea) => idea.id === ideaId);
    return callApi(`/companies/${company.id}/wishlist-ideas/order`, {
      method: 'PUT',
      jsonBody: {
        wishlistIdea: `/wishlist-ideas/${ideaId}`,
        state: column,
        position: positionForDrop(ideas, index),
      },
    });
  };

  const persistDrag = async (target: ReorderableColumn, ideaId: string) => {
    try {
      await reorderIdea(ideaId, target, columnsRef.current[target]);
      api.invalidate();
    } catch (err) {
      if (snapshotRef.current) updateColumns(snapshotRef.current);
      alert(getApiErrorMessage(err));
    } finally {
      snapshotRef.current = null;
    }
  };

  const moveIdea = async (idea: Idea) => {
    if (idea.state !== 'idea' && idea.state !== 'preparing') return;
    const source: ReorderableColumn =
      idea.state === 'preparing' ? 'preparing' : 'idea';
    const target: ReorderableColumn =
      source === 'preparing' ? 'idea' : 'preparing';
    const snapshot: ReorderableColumns = {
      idea: [...columnsRef.current.idea],
      preparing: [...columnsRef.current.preparing],
    };
    const remaining = columnsRef.current[source].filter(
      (candidate) => candidate.id !== idea.id
    );
    const targetList = [
      ...columnsRef.current[target],
      { ...idea, state: target },
    ];
    updateColumns({
      ...columnsRef.current,
      [source]: remaining,
      [target]: targetList,
    });
    try {
      await reorderIdea(idea.id, target, targetList);
      api.invalidate();
    } catch (err) {
      updateColumns(snapshot);
      alert(getApiErrorMessage(err));
    }
  };

  // Reopening a cancelled idea and unlinking one from its service order both
  // put it back on the board; the board refetches rather than updating
  // optimistically, so the idea never shows in two columns at once.
  const moveIdeaToColumn = async (idea: Idea, target: MoveDestination) => {
    try {
      const { path, options } = moveIdeaRequest(idea.id, target);
      await callApi(path, options);
      api.invalidate();
    } catch (err) {
      alert(getApiErrorMessage(err));
    }
  };

  const reopenIdea = (idea: Idea) => void moveIdeaToColumn(idea, 'idea');

  const unlinkIdea = (idea: Idea, target: MoveDestination) =>
    void moveIdeaToColumn(idea, target);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const ideaId = String(active.id);
    setActiveId(null);
    const target = findColumn(ideaId);
    if (!target) {
      snapshotRef.current = null;
      return;
    }

    if (over) {
      const overColumn = findColumn(String(over.id));
      if (overColumn === target) {
        const items = columnsRef.current[target];
        const oldIndex = items.findIndex((idea) => idea.id === ideaId);
        const newIndex = items.findIndex((idea) => idea.id === String(over.id));
        if (oldIndex >= 0 && newIndex >= 0 && oldIndex !== newIndex) {
          updateColumns({
            ...columnsRef.current,
            [target]: arrayMove(items, oldIndex, newIndex),
          });
        }
      }
    }

    void persistDrag(target, ideaId);
  };

  if (api.error) return <PageError error={api.error} />;
  if (!api.data) return <PageLoading />;

  const board = api.data;
  const cols: ReorderableColumns = columns ?? {
    idea: ideasForColumn(board.ideas, 'idea'),
    preparing: ideasForColumn(board.ideas, 'preparing'),
  };

  const cancelled = ideasForColumn(board.ideas, 'cancelled');
  const activeIdea =
    [...cols.idea, ...cols.preparing].find((idea) => idea.id === activeId) ??
    null;

  const canCancel = (idea: Idea) =>
    agency || (idea.author?.id === authUser.id && idea.state === 'idea');

  const hiddenOnMobile = (column: WishlistColumn) =>
    mobileColumn !== column ? styles.hideOnMobile : undefined;

  return (
    <PageWrap background={Color.neutral5} noPadding limitWidth="large">
      <Helmet>
        <title>{`Wishlist | ${company.name}`}</title>
      </Helmet>

      <Stack padH={1.5} padV={1.5} gap={1.5} style={styles.page}>
        <html.div>
          <Text typeface="body.small.medium" color="neutral60">
            {company.name}
          </Text>
          <Text as="h1" typeface="headline" color="neutral100">
            Wishlist
          </Text>
        </html.div>

        <html.div style={styles.switcher}>
          <ColumnSwitcher selected={mobileColumn} onSelect={setMobileColumn} />
        </html.div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
        >
          <html.div style={styles.board}>
            <BoardColumn
              column="idea"
              label="Ideas"
              count={cols.idea.length}
              emptyCopy={EMPTY_COPY.idea}
              empty={cols.idea.length === 0}
              sortableIds={cols.idea.map((idea) => idea.id)}
              style={hiddenOnMobile('idea')}
            >
              <AddIdeaCard onClick={() => setNewIdeaOpen(true)} />
              {cols.idea.map((idea) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  href={ideaHref(idea)}
                  canReorder={canReorderColumn('idea', agency)}
                  canManage={agency}
                  onOpen={openIdea}
                  onMove={agency ? moveIdea : null}
                  onLink={agency ? setLinkingIdea : null}
                  onCancel={
                    agency && canCancel(idea) ? setCancellingIdea : null
                  }
                  onReopen={null}
                />
              ))}
            </BoardColumn>

            <BoardColumn
              column="preparing"
              label="Preparing"
              count={cols.preparing.length}
              emptyCopy={EMPTY_COPY.preparing}
              empty={cols.preparing.length === 0}
              sortableIds={cols.preparing.map((idea) => idea.id)}
              style={hiddenOnMobile('preparing')}
            >
              {cols.preparing.map((idea) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  href={ideaHref(idea)}
                  canReorder={canReorderColumn('preparing', agency)}
                  canManage={agency}
                  onOpen={openIdea}
                  onMove={agency ? moveIdea : null}
                  onLink={agency ? setLinkingIdea : null}
                  onCancel={
                    agency && canCancel(idea) ? setCancellingIdea : null
                  }
                  onReopen={null}
                />
              ))}
            </BoardColumn>

            <BoardColumn
              column="service_order"
              label="Service Orders"
              count={board.serviceOrders.length}
              emptyCopy={EMPTY_COPY.service_order}
              empty={board.serviceOrders.length === 0}
              sortableIds={[]}
              style={hiddenOnMobile('service_order')}
            >
              {board.serviceOrders.map((order) => (
                <ServiceOrderCard
                  key={order['@id']}
                  order={order}
                  ideas={serviceOrderIdeas(board.ideas, order)}
                  hrefForIdea={ideaHref}
                  onOpenIdea={openIdea}
                  canManage={agency}
                  onUnlink={agency ? unlinkIdea : null}
                />
              ))}
            </BoardColumn>

            <BoardColumn
              column="cancelled"
              label="Cancelled"
              count={cancelled.length}
              emptyCopy={EMPTY_COPY.cancelled}
              empty={cancelled.length === 0}
              sortableIds={[]}
              style={hiddenOnMobile('cancelled')}
            >
              {cancelled.map((idea) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  href={ideaHref(idea)}
                  canReorder={false}
                  canManage={agency}
                  onOpen={openIdea}
                  onMove={null}
                  onLink={null}
                  onCancel={null}
                  onReopen={agency ? reopenIdea : null}
                />
              ))}
            </BoardColumn>
          </html.div>

          <DragOverlay>
            {activeIdea ? (
              <html.div style={styles.overlayCard}>
                <IdeaCardContent
                  idea={activeIdea}
                  href={ideaHref(activeIdea)}
                  canManage={agency}
                  grip={null}
                  overlay
                  onOpen={() => undefined}
                  onMove={null}
                  onLink={null}
                  onCancel={null}
                  onReopen={null}
                />
              </html.div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </Stack>

      {newIdeaOpen && (
        <NewIdeaModal
          invalidate={api.invalidate}
          onClose={() => setNewIdeaOpen(false)}
        />
      )}
      {linkingIdea && (
        <LinkServiceOrderModal
          idea={linkingIdea}
          invalidate={api.invalidate}
          onClose={() => setLinkingIdea(null)}
        />
      )}
      {cancellingIdea && (
        <CancelIdeaModal
          ideaId={cancellingIdea.id}
          invalidate={api.invalidate}
          onClose={() => setCancellingIdea(null)}
        />
      )}
    </PageWrap>
  );
}

function AddIdeaCard({ onClick }: { onClick: () => void }) {
  return (
    <html.button style={styles.addCard} onClick={onClick}>
      <PlusIcon size={18} color={color.neutral60} />
      <Text typeface="body.medium" color="neutral60">
        New idea
      </Text>
    </html.button>
  );
}

const styles = css.create({
  page: {
    minHeight: '100vh',
  },
  switcher: {
    display: {
      default: 'none',
      '@media (max-width: 768px)': 'block',
    },
  },
  board: {
    display: 'flex',
    flexDirection: {
      default: 'row',
      '@media (max-width: 768px)': 'column',
    },
    gap: 16,
    flex: 1,
    minHeight: 0,
    overflowX: 'auto',
  },
  hideOnMobile: {
    display: {
      default: 'flex',
      '@media (max-width: 768px)': 'none',
    },
  },
  overlayCard: {
    width: 258,
  },
  addCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: color.neutral20,
    borderRadius: 12,
    backgroundColor: color.neutral0,
    cursor: 'pointer',
  },
});
